import React from 'react';
import {useTranslation} from 'react-i18next';
import {TscButtonType, Checkbox} from '@techsmith/tsc-cloud-style-guide';
import logging from '../../service/logging';
import '../../static/css/modal.less';
import {IWorkerState} from '../../model/workerState';
import {StyledWarning} from '../util/StyledElements';
import AlertModalSpinner from '../util/AlertModalSpinner';
import styled from 'styled-components';
import {ITeamModel} from '../../model/appModel';
import {AlertModalPortal} from '../util/ModalPortalHelper';
import teamApi from '../../service/teamApi';
import {useTeamsStore} from '../../context/teamsStore';

const AlertBody = styled.div`
   white-space: pre-wrap;
   word-break: break-word;
`;

const DeleteContainer = styled.div`
   display: flex;
   text-align: left;
`;

const DeleteText = styled.span`
   margin: 0.5rem 0 0 0.5rem;
`;

const CheckBoxContainer = styled.div`
   margin-top: .5rem;
   margin-right: .5rem;
`;

const DeleteTeamModal: React.FC<IDeleteTeamModalProps> = ({team, onClose, onDelete}) => {
   const {t} = useTranslation();
   const [deleteConfirm, setDeleteConfirm] = React.useState(false);

   const removeTeam = useTeamsStore(s => s.removeTeam);

   const [state, setState] = React.useState({
      isWorking: false,
      hasError: false
   } as IWorkerState);

   const onDeleteClick = async () => {
      if (!deleteConfirm) {
         return;
      }

      setState({...state, isWorking: true, hasError: false});

      try {
         await teamApi.deleteTeam(team.TeamId);
         removeTeam(team.TeamId);
         onDelete();
         onClose();
      } catch (e) {
         logging.error(e);
         setState({...state, isWorking: false, hasError: true});
      }
   };

   return (
      <AlertModalPortal
         title={t('team.modal.delete.title', {teamName: team.Name})}
         onClose={() => onClose()}
         onConfirm={onDeleteClick}
         confirmButtonType={TscButtonType.destructive}
         confirmContent={t('general.delete')}
         onDismiss={() => onClose()}
         dismissButtonType={TscButtonType.secondary}
         dismissContent={t('general.cancel')}
         hideButtons={state.isWorking}
         disableConfirmButton={!deleteConfirm}
         modalWidth="30rem"
      >
         {state.isWorking && <AlertModalSpinner />}
         <AlertBody>
            {state.hasError && (
               <StyledWarning>
                  {t('general.errorText')}
               </StyledWarning>
            )}
            {t('team.modal.delete.warning', {teamName: team.Name})}

            <DeleteContainer>
               <CheckBoxContainer>
                  <Checkbox
                     isChecked={deleteConfirm}
                     a11Text={t('general.delete')}
                     onClick={() => {
                        setDeleteConfirm(!deleteConfirm);
                     }}
                  />
               </CheckBoxContainer>

               <DeleteText>
                  {t('team.modal.delete.confirmCheckboxText')}
               </DeleteText>
            </DeleteContainer>
         </AlertBody>
      </AlertModalPortal>
   );
};

export default DeleteTeamModal;

export interface IDeleteTeamModalProps {
   team: ITeamModel;
   onClose: () => void;
   onDelete: () => void;
}
